/* "Personalization level" — how many sessions of this agent we've observed, and
 * therefore how much trajectory data our optimization recommendations rest on.
 * Behind the `ig-personalization-level` flag: the thresholds below are a product
 * decision, not a fitted model, and nothing downstream consumes them yet
 * (insights are hand-authored today), so the copy promises sharper
 * recommendations over time rather than a model that has already learned.
 *
 * Sessions are the only signal. Distinct sessions in the window:
 *   0–9   -> getting started
 *   10–49 -> tuned
 *   50+   -> optimized
 *
 * The ids stay low/medium/high because the scale is ordinal — the display words
 * live in PERSONALIZATION_LABEL, and `?plevel=` previews keep working. */

export type PersonalizationLevel = "low" | "medium" | "high";

/** Observation window. Fixed, NOT the page's date picker — see
 *  usePersonalizationSignals. 30 stays under the API's
 *  ANALYTICS_MAX_WINDOW_DAYS (31). */
export const PERSONALIZATION_WINDOW_DAYS = 30;

export interface PersonalizationSignals {
  /** Distinct sessions in the window (AgentRollup.session_count). */
  sessions: number;
}

/** Session counts that open each level. Both are inclusive floors — 10 sessions
 *  is already tuned, 50 is already optimized. */
export const PERSONALIZATION_GATES = {
  /** Sessions >= this reads as tuned. */
  medium: 10,
  /** Sessions >= this reads as optimized. */
  high: 50,
} as const;

const ORDER: readonly PersonalizationLevel[] = ["low", "medium", "high"];

/** 1-based position of a level, for the segmented bar's fill count. */
export function levelIndex(level: PersonalizationLevel): number {
  return ORDER.indexOf(level) + 1;
}

export const PERSONALIZATION_LABEL: Record<PersonalizationLevel, string> = {
  low: "Getting started",
  medium: "Tuned",
  high: "Optimized",
};

export function resolveLevel(s: PersonalizationSignals): PersonalizationLevel {
  if (s.sessions >= PERSONALIZATION_GATES.high) return "high";

  if (s.sessions >= PERSONALIZATION_GATES.medium) return "medium";

  return "low";
}

/** Tooltip on the ⓘ. */
export const PERSONALIZATION_HINT =
  "As this agent runs, we study its trajectories to find which context optimizations reduce its spend. The more sessions we see, the more tailored our recommendations get.";

/** Caption under the bar — one line per milestone, so the sentence reports the
 *  level the bar is showing rather than always asking for more traffic. */
export const PERSONALIZATION_CAPTION: Record<PersonalizationLevel, string> = {
  low: "Building personalization from agent activity.",
  medium:
    "Keep working with your agent, recommendations sharpen as we learn more.",
  high: "Your agent now delivers our most personalized recommendations.",
};
